import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import {
  BookingDetails,
  EventWaitlistFilters,
  EventWaitlistSummary,
  PrivateEventBooking,
  PrivateEventBookingReservation,
  ReservationDetails,
  ReservationPartDetails,
  RosterInviteFirstBill,
  ScheduledEvent,
  ScheduledEventFilters
} from '../../models/scheduled-event';

import { ApiService } from '../api/api.service';
import { ListResponse } from '../../models/list-response';
import { getHttpParamsForFilters, propExists } from '../../utils';

function addBypassRestrictions(requestBody: any, bypassRestrictions?: string[]) {
  if (bypassRestrictions && (bypassRestrictions.length > 0)) {
    requestBody.bypass_restriction = bypassRestrictions;
  }
}

export interface ReservationPartCheckInResponse {
  is_checked_in_to_gym: boolean;
}

export interface BookEventDirectlyReservationPart {
  reservation_part_id: number;
  event_part_id: number;
}

export interface BookEventDirectlyResponse {
  booking_id: number;
  reservation_id: number;
  // One entry per event part, so the caller can act on the reservation part for
  // a specific event part (e.g. checking the participant in immediately).
  reservation_parts: BookEventDirectlyReservationPart[];
}

export interface PrivateEventRequestFormInfoResponse {
  event_type_name: string;
  event_id: number;
  first_event_part_id: number;
}

@Injectable({
  providedIn: 'root'
})
export class ScheduledEventService {
  /**
   * A service providing access to scheduled events and their reservations via
   * the API.
   */

  constructor(
    private apiService: ApiService
  ) {}

  /**
   * Get the set of scheduled events of the given type which are happening on the given ISO date.
   */
  getScheduledEvents(date: string, eventTypeId: number, isPrivate: boolean): Promise<ScheduledEvent[]> {
    let params = new HttpParams();
    params = params.append('date', date);
    params = params.append('event_type_id', eventTypeId.toString());
    params = params.append('is_private', isPrivate ? 'true' : 'false');

    return this.apiService.getFromAPI<ScheduledEvent[]>(
      'gym-endpoints/scheduled-events/',
      params
    );
  }

  /**
   * Get a page of per-event waitlist summaries (Waiting and Pending counts).
   */
  getEventWaitlistSummaries(page: number,
                            pageSize: number,
                            params: EventWaitlistFilters): Promise<ListResponse<EventWaitlistSummary>> {
    return this.apiService.getCollection<EventWaitlistSummary, EventWaitlistFilters>(
      'event-waitlist-summaries/',
      page,
      pageSize,
      params
    );
  }

  /**
   * Get the set of scheduled events which are happening now (or due to start in
   * the next 15 minutes).
   */
  getCurrentScheduledEvents(filters: ScheduledEventFilters): Promise<ScheduledEvent[]> {
    let params = getHttpParamsForFilters(filters);

    params = params.append('when', 'now');

    return this.apiService.getFromAPI<ScheduledEvent[]>(
      'gym-endpoints/scheduled-events/',
      params
    );
  }

  /**
   * Get the set of scheduled events which are happening in the future, on the
   * given ISO date.
   */
  getFutureScheduledEvents(date: string,
                           filters: ScheduledEventFilters): Promise<ScheduledEvent[]> {
    let params = getHttpParamsForFilters(filters);

    params = params.append('when', 'future');
    params = params.append('date', date);

    return this.apiService.getFromAPI<ScheduledEvent[]>(
      'gym-endpoints/scheduled-events/',
      params
    );
  }

  /**
   * Get the set of scheduled events which are happening in the past, on the
   * given ISO date.
   */
  getPastScheduledEvents(date: string,
                         filters: ScheduledEventFilters): Promise<ScheduledEvent[]> {
    let params = getHttpParamsForFilters(filters);

    params = params.append('when', 'past');
    params = params.append('date', date);

    return this.apiService.getFromAPI<ScheduledEvent[]>(
      'gym-endpoints/scheduled-events/',
      params
    );
  }

  /**
   * Get the set of all scheduled events which are happening on the given ISO date.
   */
  getAllScheduledEvents(date: string,
                        filters: ScheduledEventFilters): Promise<ScheduledEvent[]> {
    let params = getHttpParamsForFilters(filters);
    params = params.append('date', date);

    return this.apiService.getFromAPI<ScheduledEvent[]>(
      'gym-endpoints/scheduled-events/',
      params
    );
  }

  /**
   * Get a specific scheduled event.
   *
   * @param includeNonpaymentCancelled - PRO-1225: When true, reservations
   *   which were auto-cancelled due to nonpayment (stale, unpaid bookings) are
   *   included in the event's reservations list. They are hidden by default.
   */
  getScheduledEvent(eventPartId: number,
                    includeNonpaymentCancelled = false): Promise<ScheduledEvent> {
    let params = new HttpParams();
    if (includeNonpaymentCancelled) {
      params = params.append('include_nonpayment_cancelled', 'true');
    }

    return this.apiService.getFromAPI<ScheduledEvent>(
      'gym-endpoints/get-scheduled-event/' + eventPartId.toString() + '/',
      params
    );
  }

  /**
   * Get detailed reservation + booking info for a single reservation part,
   * backing the participant details modal.
   */
  getReservationPartDetails(reservationPartId: number): Promise<ReservationPartDetails> {
    return this.apiService.getFromAPI<ReservationPartDetails>(
      'gym-endpoints/get-reservation-part-details/' + reservationPartId.toString() + '/'
    );
  }

  /**
   * Get the whole booking behind the booking details modal.
   */
  getBookingDetails(bookingId: number): Promise<BookingDetails> {
    return this.apiService.getFromAPI<BookingDetails>(
      'gym-endpoints/get-booking-details/' + bookingId.toString() + '/'
    );
  }

  /**
   * Get one participant's whole reservation (every session), backing the
   * reservation mode of the participant details modal.
   */
  getReservationDetails(reservationId: number): Promise<ReservationDetails> {
    return this.apiService.getFromAPI<ReservationDetails>(
      'gym-endpoints/get-reservation-details/' + reservationId.toString() + '/'
    );
  }

  /**
   * Set the check in status of the given reservation part.
   *
   * @param reservationPartId - The ID of the reservation part to set.
   * @param hasCheckedIn - Whether the reservation part has been checked in.
   */
  setReservationPartCheckIn(reservationPartId: number, hasCheckedIn: boolean): Promise<ReservationPartCheckInResponse> {
    return this.apiService.postToAPI<ReservationPartCheckInResponse>(
      'gym-endpoints/set-reservation-check-in/',
      {
        reservation_part_id: reservationPartId,
        has_checked_in: hasCheckedIn
      }
    );
  }

  /**
   * Get the booking for the given private event.
   */
  getPrivateEventBooking(privateEventId: number): Promise<PrivateEventBooking> {
    return this.apiService.getFromAPI<PrivateEventBooking>(
      'gym-endpoints/get-private-event-booking/' + privateEventId.toString() + '/'
    );
  }

  /**
   * Set the booking info for the given private event (this will create a
   * booking if one does not already exist for the event).
   */
  setPrivateEventBooking(privateEventId: number, booking: PrivateEventBooking): Promise<PrivateEventBooking> {
    return this.apiService.postToAPI<PrivateEventBooking>(
      'gym-endpoints/set-private-event-booking/' + privateEventId.toString() + '/',
      booking
    );
  }

  /**
   * Add a single participant to a private event's booking, filling the first
   * open reservation slot or increasing the event's capacity by 1 if it is
   * full. Returns the new reservation's IDs (including a reservation part ID per
   * event part) so the caller can check the participant in immediately.
   */
  addParticipantToPrivateEvent(privateEventId: number,
                               participant: PrivateEventBookingReservation): Promise<BookEventDirectlyResponse> {
    return this.apiService.postToAPI<BookEventDirectlyResponse>(
      'gym-endpoints/add-participant-to-private-event/' + privateEventId.toString() + '/',
      participant
    );
  }

  /**
   * Book the given customer directly onto the given event, bypassing fees,
   * add-ons, and participant questions.
   */
  bookCustomerOntoEventById(eventId: number,
                            customerId: number,
                            sendConfirmationEmail: boolean,
                            // Omitted (rather than sent false) when the caller has no UI for it,
                            // so the server leaves its existing default behaviour alone (PRO-1512).
                            includesEntry?: boolean): Promise<BookEventDirectlyResponse> {
    return this.apiService.postToAPI<BookEventDirectlyResponse>(
      'gym-endpoints/book-event-directly/' + eventId.toString() + '/' + customerId.toString() + '/',
      {
        send_confirmation_email: sendConfirmationEmail,
        ...(typeof includesEntry === 'boolean' ? { includes_entry: includesEntry } : {})
      }
    );
  }

  /**
   * Book the given customer directly onto the given event, bypassing fees,
   * add-ons, and participant questions.
   */
  bookCustomerOntoEventByNameAndBirthday(eventId: number,
                                         firstName: string,
                                         lastName: string,
                                         birthday: string,
                                         email: string | null,
                                         sendConfirmationEmail: boolean,
                                         includesEntry?: boolean): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/book-event-directly-by-name-and-birthday/' + eventId.toString() + '/',
      {
        first_name: firstName,
        last_name: lastName,
        birthday,
        email,
        send_confirmation_email: sendConfirmationEmail,
        ...(typeof includesEntry === 'boolean' ? { includes_entry: includesEntry } : {})
      }
    ).then();
  }

  /**
   * Add the given customer directly onto the given event's waitlist, bypassing
   * participant questions.
   */
  addCustomerToEventWaitlist(eventId: number,
                             customerId: number,
                             notificationEmail: string,
                             bypassRestrictions?: string[]): Promise<void> {
    const requestBody: any = {
      notification_email: notificationEmail
    };
    addBypassRestrictions(requestBody, bypassRestrictions);

    return this.apiService.postToAPI<any>(
      'gym-endpoints/add-customer-to-event-waitlist-directly/' + eventId.toString() + '/' + customerId.toString() + '/',
      requestBody
    ).then();
  }

  /**
   * Add the given customer directly onto the given event type's waitlist.
   */
  addCustomerToEventTypeWaitlist(eventTypeId: number,
                                 customerId: number,
                                 notificationEmail: string,
                                 preferredDays: string,
                                 preferredLocations: string,
                                 bypassRestrictions?: string[]): Promise<void> {
    const requestBody: any = {
      notification_email: notificationEmail,
      preferred_days_of_week: preferredDays,
      preferred_locations: preferredLocations,
    };
    addBypassRestrictions(requestBody, bypassRestrictions);

    return this.apiService.postToAPI<any>(
      'gym-endpoints/add-customer-to-event-type-waitlist-directly/' + eventTypeId.toString() + '/' + customerId.toString() + '/',
      requestBody
    ).then();
  }

  /**
   * Send (or re-send) an invitation to book to an existing waitlist entry.
   *
   * `newDeadlineDate`/`newDeadlineTime` are optional. When both are left
   * blank, the entry's existing booking deadline is left untouched if it is
   * already pending, and otherwise a fresh one is derived from the event
   * type's configured acceptance window.
   *
   * An `inviteNote` of undefined leaves the entry's stored note in place, so a
   * re-send repeats it; passing a string (including '') replaces it.
   *
   * With `resendOnly`, the server refuses (a 400 string) an entry that is no
   * longer pending instead of re-inviting it.
   */
  inviteWaitlistEntryToBook(waitlistEntryId: string,
                            eventId?: number,
                            newDeadlineDate?: string,
                            newDeadlineTime?: string,
                            inviteNote?: string,
                            sendInviteToParentsAndGuardians = false,
                            bypassRestrictions?: string[],
                            resendOnly = false): Promise<void> {
    const requestBody: any = {};
    if (eventId) {
      requestBody.event_id = eventId;
    }
    if (newDeadlineDate && newDeadlineTime) {
      requestBody.new_deadline_date = newDeadlineDate;
      requestBody.new_deadline_time = newDeadlineTime;
    }
    // Sent whenever defined, empty string included -- that is how staff clear
    // the note. Omitted entirely, the entry keeps the note it already has.
    if (inviteNote !== undefined) {
      requestBody.invite_note = inviteNote;
    }
    if (sendInviteToParentsAndGuardians) {
      requestBody.send_invite_to_parents_and_guardians = true;
    }
    addBypassRestrictions(requestBody, bypassRestrictions);
    if (resendOnly) {
      requestBody.resend_only = true;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/invite-waitlist-entry-to-book/' + waitlistEntryId + '/',
      requestBody
    ).then();
  }

  /**
   * Send (or re-send) an invitation to join a roster to an existing waitlist
   * entry.
   *
   * An omitted `startDate` leaves the entry's existing start date alone, so
   * re-sending an invitation does not quietly discard it. Pass
   * `clearStartDate` to remove it deliberately. See inviteWaitlistEntryToBook
   * above for the deadline arguments.
   */
  inviteWaitlistEntryToRoster(waitlistEntryId: string,
                              rosterId: number,
                              startDate: string | null,
                              clearStartDate = false,
                              newDeadlineDate?: string,
                              newDeadlineTime?: string,
                              inviteNote?: string,
                              sendInviteToParentsAndGuardians = false): Promise<void> {
    const requestBody: any = {
      roster_id: rosterId
    };
    if (startDate) {
      requestBody.start_date = startDate;
    } else if (clearStartDate) {
      requestBody.clear_start_date = true;
    }
    if (newDeadlineDate && newDeadlineTime) {
      requestBody.new_deadline_date = newDeadlineDate;
      requestBody.new_deadline_time = newDeadlineTime;
    }
    // See inviteWaitlistEntryToBook above.
    if (inviteNote !== undefined) {
      requestBody.invite_note = inviteNote;
    }
    if (sendInviteToParentsAndGuardians) {
      requestBody.send_invite_to_parents_and_guardians = true;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/invite-waitlist-entry-to-book/' + waitlistEntryId + '/',
      requestBody
    ).then();
  }

  /** `newDeadlineDate`/`newDeadlineTime` are in the event's location zone; send them or `hoursToAccept`, not both. */
  inviteCustomerToBookEvent(eventId: number,
                            customerId: number,
                            hoursToAccept: number | null,
                            inviteNote: string,
                            sendInviteToParentsAndGuardians: boolean,
                            newDeadlineDate?: string,
                            newDeadlineTime?: string,
                            bypassRestrictions?: string[]): Promise<void> {
    const requestBody: any = {};
    if (hoursToAccept) {
      requestBody.hours_to_accept = hoursToAccept;
    }
    if (newDeadlineDate && newDeadlineTime) {
      requestBody.new_deadline_date = newDeadlineDate;
      requestBody.new_deadline_time = newDeadlineTime;
    }
    if (inviteNote) {
      requestBody.invite_note = inviteNote;
    }
    if (sendInviteToParentsAndGuardians) {
      requestBody.send_invite_to_parents_and_guardians = sendInviteToParentsAndGuardians;
    }
    addBypassRestrictions(requestBody, bypassRestrictions);

    return this.apiService.postToAPI<any>(
      'gym-endpoints/invite-customer-to-book-event/' + eventId.toString() + '/' + customerId.toString() + '/',
      requestBody
    ).then();
  }

  /** `newDeadlineDate`/`newDeadlineTime` are in the roster's location zone; send them or `hoursToAccept`, not both. */
  inviteCustomerToJoinRoster(rosterId: number,
                             customerId: number,
                             startDate: string | null,
                             hoursToAccept: number | null,
                             inviteNote: string,
                             sendInviteToParentsAndGuardians: boolean,
                             newDeadlineDate?: string,
                             newDeadlineTime?: string): Promise<void> {
    const requestBody: any = {};
    if (startDate) {
      requestBody.start_date = startDate;
    }
    if (hoursToAccept) {
      requestBody.hours_to_accept = hoursToAccept;
    }
    if (newDeadlineDate && newDeadlineTime) {
      requestBody.new_deadline_date = newDeadlineDate;
      requestBody.new_deadline_time = newDeadlineTime;
    }
    if (inviteNote) {
      requestBody.invite_note = inviteNote;
    }
    if (sendInviteToParentsAndGuardians) {
      requestBody.send_invite_to_parents_and_guardians = sendInviteToParentsAndGuardians;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/invite-customer-to-join-roster/' + rosterId.toString() + '/' + customerId.toString() + '/',
      requestBody
    ).then();
  }

  /**
   * Get the pro-rated first bill preview for inviting a customer to the given
   * roster (PRO-1458). Pass `startDate` to also get the specific proration
   * for that date; leave it null to get only the roster's billing shape.
   */
  getRosterInviteFirstBill(rosterId: number, startDate: string | null): Promise<RosterInviteFirstBill> {
    let params = new HttpParams();
    if (startDate) {
      params = params.append('start_date', startDate);
    }

    return this.apiService.getFromAPI<RosterInviteFirstBill>(
      'gym-endpoints/roster-invite-first-bill/' + rosterId.toString() + '/',
      params
    );
  }

  expireWaitlistEntryImmediately(waitlistEntryId: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/expire-waitlist-entry-immediately/' + waitlistEntryId + '/',
      {}
    ).then();
  }

  revertWaitlistEntryToWaiting(waitlistEntryId: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/revert-waitlist-entry-to-waiting/' + waitlistEntryId + '/',
      {}
    ).then();
  }

  modifyWaitlistEntryBookingDeadline(waitlistEntryId: string,
                                     newDeadlineDate: string,
                                     newDeadlineTime: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/modify-waitlist-entry-booking-deadline/' + waitlistEntryId + '/',
      {
        new_deadline_date: newDeadlineDate,
        new_deadline_time: newDeadlineTime
      }
    ).then();
  }

  removeWaitlistEntry(waitlistEntryId: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/remove-waitlist-entry/' + waitlistEntryId + '/',
      {}
    ).then();
  }

  /**
   * Get the start and end timestamp of all future event parts of the given
   * event type which have reservations.
   *
   * This info is displayed to the user in the confirmation modal before they
   * retire an event type.
   */
  getFutureEventPartsWithReservations(eventTypeId: number): Promise<{start_datetime: string, end_datetime: string}[]> {
    return this.apiService.getFromAPI<{start_datetime: string, end_datetime: string}[]>(
      'gym-endpoints/future-event-parts-with-reservations/' + eventTypeId.toString() + '/'
    );
  }

  getRequestFormInfoForPrivateEvent(privateEventId: number): Promise<PrivateEventRequestFormInfoResponse> {
    return this.apiService.getFromAPI<PrivateEventRequestFormInfoResponse>(
      'get-request-form-info-for-private-event/' + privateEventId.toString() + '/'
    );
  }
}
